import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { consumeDeletedFlag } from '../auth/session';
import { toMessage } from '../utils/errors';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';

const APP_NAME = process.env.REACT_APP_APP_NAME || 'Ghost Daddy';
// Kept declared so the support block below can be re-enabled by uncommenting.
// eslint-disable-next-line no-unused-vars
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || '';

export default function Login() {
  const auth = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justDeleted, setJustDeleted] = useState(false);

  // Set by the profile page right before the session is torn down.
  useEffect(function () {
    setJustDeleted(consumeDeletedFlag());
  }, []);

  function onSubmit(event) {
    event.preventDefault();

    if (busy) return;

    setError(null);
    setJustDeleted(false);
    setBusy(true);

    auth.signIn(email.trim(), password).catch(function (err) {
      setBusy(false);
      setError(toMessage(err, 'Unable to sign in. Please try again.'));
    });
    // On success the route guard swaps this page out for the profile,
    // so `busy` is intentionally left on.
  }

  return (
    <div className="page page--centered">
      <main className="card card--auth">
        <header className="auth__header">
          <div className="brand">{APP_NAME}</div>
          <h1 className="auth__title">Delete your account</h1>
          <p className="auth__subtitle">
            Sign in with your {APP_NAME} app credentials to review your account
            details and permanently delete your account and personal data.
          </p>
        </header>

        {justDeleted ? (
          <Alert tone="success">
            Your account and personal data have been permanently deleted. You
            have been signed out.
          </Alert>
        ) : null}

        {error ? <Alert tone="error">{error}</Alert> : null}

        <form onSubmit={onSubmit} noValidate>
          <label className="field">
            <span className="field__label">Email address</span>
            <input
              className="field__input"
              type="email"
              name="email"
              value={email}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck="false"
              required
              disabled={busy}
              placeholder="you@example.com"
              onChange={function (event) {
                setEmail(event.target.value);
              }}
            />
          </label>

          <label className="field">
            <span className="field__label">Password</span>
            <div className="field__group">
              <input
                className="field__input"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={password}
                autoComplete="current-password"
                required
                disabled={busy}
                placeholder="••••••••"
                onChange={function (event) {
                  setPassword(event.target.value);
                }}
              />
              <button
                type="button"
                className="field__toggle"
                onClick={function () {
                  setShowPassword(!showPassword);
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          <button
            type="submit"
            className="button button--primary button--block"
            disabled={busy || !email || !password}
          >
            {busy ? <Spinner light /> : null}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <footer className="auth__footer">
          <p>
            This page exists solely to let you delete your {APP_NAME} account
            and the personal data held with it. Nothing is deleted until you
            confirm on the next screen.
          </p>
          {/* Support contact - uncomment once a support mailbox exists,
              and restore REACT_APP_SUPPORT_EMAIL in .env.
          {SUPPORT_EMAIL ? (
            <p>
              Trouble signing in?{' '}
              <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>
            </p>
          ) : null}
          */}
        </footer>
      </main>
    </div>
  );
}
